<svelte:options immutable></svelte:options>
<script lang="ts">
    import { grid_fitValue } from './grid';
    import type { Node, NodeRect } from './state/node';
    import type { Transition } from './state/transition';
    import { line_intersectWithHorizontalLine, line_intersectWithVerticalLine, Vector, Vector_90Right, Vector_center, Vector_normalize } from './Vector';
    
    import { getPositionInPane } from './context';
    const positionInPane = getPositionInPane();


    export let transition: Transition;
    export let fromState: Node;
    export let toState: Node;

    export let color: string;
    export let strokeWidth: string;
    export let error: boolean = false;
    export let marker: string;

    export let enableDrag: boolean;
    export let showContactArea: boolean;

    export let onClick: () => void;
    export let onMoveControlPointOffset: (controlPointOffset: number) => void;


    const radius = 20;

    let controlPoint = {x: 0, y: 0};
    let m = {x: 0, y: 0};

    let p1a = {x: 0, y: 0};
    let p2a = {x: 0, y: 0};

    let mce = {x: 0, y:0};

    // Berechnet den Winkel zwischen der x-Achse und dem Vektor (x, y)
    // Ausgabe: Winkel in Grad, im Bereich [0, 360]
    // Die Ausgabe 360° kann auftreten bei einem Winkel, der in Wirklichkeit fast 360° ist, aber aufgrund von Rundungsfehlern als 360° ausgegeben wird.
    function phi(x: number, y: number) {
        const phi_ = Math.atan2(y, x) * 180 / Math.PI;
        if (phi_ < 0) {
            return phi_ + 360;
        } else {
            return phi_;
        }
    }

    function anfangspunktRect(node: NodeRect, controlPoint: Vector){
        const phi_controlPoint = phi(controlPoint.x - node.posx, -(controlPoint.y - node.posy));
        // console.log(phi_controlPoint);

        const phi1 = phi(node.width/2 , node.height/2);
        const phi2 = phi(-node.width/2 , node.height/2);
        const phi3 = phi(-node.width/2 , -node.height/2);
        const phi4 = phi(node.width/2 , -node.height/2);
        // console.log(phi1, ' ', phi2, ' ', phi3, ' ', phi4);

        if (phi_controlPoint < phi1) {
            // rechte Seite
            return line_intersectWithVerticalLine({x: node.posx, y: node.posy}, controlPoint, node.posx + node.width/2);
        } else if (phi_controlPoint < phi2) {
            // Oberseite
            return line_intersectWithHorizontalLine({x: node.posx, y: node.posy}, controlPoint, node.posy - node.height/2);
        } else if (phi_controlPoint < phi3) {
            // linke Seite
            return line_intersectWithVerticalLine({x: node.posx, y: node.posy}, controlPoint, node.posx - node.width/2);
        } else if (phi_controlPoint < phi4) {
            // Unterseite
            return line_intersectWithHorizontalLine({x: node.posx, y: node.posy}, controlPoint, node.posy + node.height/2);
        } else {
            // rechte Seite
            return line_intersectWithVerticalLine({x: node.posx, y: node.posy}, controlPoint, node.posx + node.width/2);
        }
    }

    function anfangspunktCircle(controlPoint: Vector, p: Vector, radius: number): Vector {
        const pc = {
            x: controlPoint.x - p.x,
            y: controlPoint.y - p.y,
        }

        let pce = Vector_normalize(pc);

        let pa = {
            x: p.x + pce.x * radius,
            y: p.y + pce.y * radius,
        }
        return pa;
    }


    $:{
        // Kontrollpunkt berechnen
        let p1 = {x: fromState.posx, y: fromState.posy};
        let p2 = {x: toState.posx,   y: toState.posy  };

        m = Vector_center(p1, p2);

        let p1m = {
            x: 1/2 * (p2.x - p1.x),
            y: 1/2 * (p2.y - p1.y),
        }

        let p1me = Vector_normalize(p1m);

        mce = Vector_90Right(p1me);

        controlPoint = {
            x: m.x + mce.x * transition.controlPointOffset,
            y: m.y + mce.y * transition.controlPointOffset,
        }

        // Anfangspunkt berechnen
        p1a = fromState.type === 'NodeRect' ? anfangspunktRect(fromState, controlPoint) : anfangspunktCircle(controlPoint, p1, radius);

        // Endpunkt berechnen
        p2a = toState.type === 'NodeRect' ? anfangspunktRect(toState, controlPoint) : anfangspunktCircle(controlPoint, p2, radius);
    }

    // Position des Ankers am Text berechnen
    $: anchorPosition = (() => {
        if (transition.controlPointOffset >= 0) {
            // console.log('mce ', mce);
            let angle = Math.atan2(mce.y, mce.x) * 180 / Math.PI;
            let ap = Math.round((-angle + 360) / 45) % 8;
            // let ap = Math.round(angle / 45) % 8; 
            // console.log('Winkel ', angle)
            // console.log('Ankerposition ', ap)
            return ap;
        } else {
            // console.log('mce ', mce);
            let angle = Math.atan2(-mce.y, -mce.x) * 180 / Math.PI;
            let ap = Math.round((-angle + 360) / 45) % 8;
            // let ap = Math.round(angle / 45) % 8; 
            // console.log('Winkel ', angle)
            // console.log('Ankerposition ', ap)
            return ap;
        }
    })();

    let pathElement;

    let dragStartOffset = 0;
    let dragStartControlPointOffset = 0;

    let lastCapturedPointerId: number | undefined = undefined;
    let pointerHasBeenMoved = false;

    function handlePointerDown(event: PointerEvent) {
        // console.log('transition - down')

        if (!event.isPrimary) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        pathElement.setPointerCapture(event.pointerId);
        lastCapturedPointerId = event.pointerId;

        pointerHasBeenMoved = false;

        dragStartControlPointOffset = transition.controlPointOffset;

        const pointerPosition = positionInPane(event.clientX, event.clientY);

        // Position des Zeigers relativ zum Mittelpunkt der direkten Verbindungslinie von P1 und P2
        const m_p = {
            x: pointerPosition.x - m.x,
            y: pointerPosition.y - m.y,
        }

        // Zeigerposition auf die Kontrollpunktachse projizieren
        // Skalarprodukt zwischen dem Einheitsvektor und der Position des Zeigers relativ zum Mittelpunkt
        dragStartOffset = m_p.x * mce.x + m_p.y * mce.y;
    }

    function handlePointerMove(event: PointerEvent) {
        // console.log('transition - handlePointerMove()');
        if (!event.isPrimary) {
            return;
        }

        if (lastCapturedPointerId !== event.pointerId) {
            // console.log('BaseNode - pointerMove - Das ist nicht der Pointer, der zuletzt gecaptured wurde.');
            return;
        }

        pointerHasBeenMoved = true;

        if (enableDrag) {
            const pointerPosition = positionInPane(event.clientX, event.clientY);
            
            // Position des Zeigers relativ zum Mittelpunkt der direkten Verbindungslinie von P1 und P2
            const m_p = {
                x: pointerPosition.x - m.x,
                y: pointerPosition.y - m.y,
            }

            // Zeigerposition auf die Kontrollpunktachse projizieren
            // Skalarprodukt zwischen dem Einheitsvektor und der Position des Zeigers relativ zum Mittelpunkt
            const offset = m_p.x * mce.x + m_p.y * mce.y;

            // Verschiebung des Pointers entlang der Kontrollpunktachse berechnen (verglichen mit dem Moment, als der Pointer gedrückt wurde)
            const delta = offset - dragStartOffset;

            // Korrekturfaktor 2, damit sich die Verschiebung natürlicher anfühlt
            onMoveControlPointOffset(grid_fitValue(dragStartControlPointOffset + delta*2));
        }
    }

    function handlePointerUp(event: PointerEvent) {
        // console.log('BaseTransition - pointerUp');

        if (!event.isPrimary) {
            return;
        }

        if (lastCapturedPointerId !== event.pointerId) {
            // console.log('BaseTransition - pointerUp - Das ist nicht der Pointer, der zuletzt gecaptured wurde.');
            return;
        }

        if (!pointerHasBeenMoved) {
            onClick();
        }

        pointerHasBeenMoved = false;
    }

    function handleLostPointerCapture(event: PointerEvent) {
        // console.log('BaseTransition - lostPointerCapture');
        if (lastCapturedPointerId === event.pointerId) {
            lastCapturedPointerId = undefined;
        }
    }

    function handleClick(event: MouseEvent) {
        event.stopPropagation();
    }

    function bezier_pointAt(p1: Vector, p2: Vector, controlPoint: Vector, t: number): Vector {
        let x = (1-t)*(1-t)*p1.x + 2*(1-t)*t*controlPoint.x + t*t*p2.x;
        let y = (1-t)*(1-t)*p1.y + 2*(1-t)*t*controlPoint.y + t*t*p2.y;
        return {x, y};
    }


    const textPositionOffset = 5;
    $: textPosition = (() => {
        const textPositionWithoutOffset = bezier_pointAt(p1a, p2a, controlPoint, 0.5);

        if (transition.controlPointOffset >= 0) {
            return {
                x: textPositionWithoutOffset.x + mce.x * textPositionOffset,
                y: textPositionWithoutOffset.y + mce.y * textPositionOffset,
            }
        } else {
            return {
                x: textPositionWithoutOffset.x - mce.x * textPositionOffset,
                y: textPositionWithoutOffset.y - mce.y * textPositionOffset,
            }
        }
    })()

    // $: textPositionWithoutOffset = bezier_pointAt(p1a, p2a, controlPoint, 0.5);
    // $: textPosition = {
    //     x: textPositionWithoutOffset.x + mce.x * 20,
    //     y: textPositionWithoutOffset.y + mce.y * 20,
    // }
</script>

<!-- Pfad, der angezeigt wird  -->
<path
    d="M{p1a.x} {p1a.y} Q{controlPoint.x} {controlPoint.y} {p2a.x} {p2a.y}"
    stroke={color}
    stroke-width={strokeWidth}
    fill="none"
    marker-end={marker}
/>

<!-- Kontaktfläche -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
{#if showContactArea}
    <path 
        class:contactArea={!error}
        class:contactAreaError={error}
        bind:this={pathElement}
        on:pointerdown={handlePointerDown}
        on:pointermove={handlePointerMove}
        on:pointerup={handlePointerUp}
        on:click={handleClick}
        on:lostpointercapture={handleLostPointerCapture}  
        d="M{p1a.x} {p1a.y} Q{controlPoint.x} {controlPoint.y} {p2a.x} {p2a.y}"
        stroke-width="15"
        fill="none"
    />
{/if}

<!-- rotate({Math.atan2(p2a.y - p1a.y, p2a.x - p1a.x) * 180 / Math.PI}) -->
<g
    transform="translate({textPosition.x} {textPosition.y})"
>
    <slot anchorPosition={anchorPosition}/>
    <!-- <slot anchorPosition={0}/> -->
</g>

<!-- Debug: Kontrollpunkt, Mittelpunkt der direkten Verbindungslinie und "Mittelpunkt" der Bezier-Kurve zeigen  -->
<!-- <circle cx={controlPoint.x} cy={controlPoint.y} r="2" fill="red" />
<circle cx={m.x} cy={m.y} r="2" fill="red" /> -->
<!-- <circle cx={textPosition.x} cy={textPosition.y} r="2" fill="red" /> -->

<style>
    .contactArea {
        stroke-opacity: 0;
        stroke: black;
    }
    .contactArea:hover {
        stroke-opacity: 0.2;
    }

    .contactAreaError {
        stroke-opacity: 0.2;
        stroke: red;
    }    
    .contactAreaError:hover {
        stroke-opacity: 0.4;
    }
</style>